import { ArrowRight, MapPin, Clock, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Motion";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const CsbConcept = ({ c }) => (
  <div id="csb-concept" data-testid="csb-concept-site" className="border-t border-white/10">
    {/* concept nav */}
    <div className="sticky top-0 z-40 bg-midnight/85 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <p className="font-sora font-extrabold tracking-tight">
          {c.brand} <span className="text-amber-400 text-xs font-bold uppercase tracking-[0.2em] ml-1">{c.brand_tag}</span>
        </p>
        <div className="hidden md:flex items-center gap-8 text-sm text-white/60">
          <button onClick={() => scrollTo("csb-menu")} className="hover:text-white transition-colors duration-300" data-testid="csb-nav-menu">{c.nav.menu}</button>
          <button onClick={() => scrollTo("csb-story")} className="hover:text-white transition-colors duration-300" data-testid="csb-nav-story">{c.nav.story}</button>
          <button onClick={() => scrollTo("csb-locations")} className="hover:text-white transition-colors duration-300" data-testid="csb-nav-locations">{c.nav.locations}</button>
          <button onClick={() => scrollTo("csb-franchise")} className="hover:text-white transition-colors duration-300" data-testid="csb-nav-franchise">{c.nav.franchise}</button>
          <button onClick={() => scrollTo("csb-menu")} data-testid="csb-nav-order" className="rounded-full bg-amber-500 text-midnight px-5 py-2 text-sm font-bold transition-transform duration-300 hover:scale-105">
            {c.nav.order}
          </button>
        </div>
      </div>
    </div>

    {/* hero */}
    <section className="relative min-h-[85vh] flex items-end overflow-hidden" data-testid="csb-hero">
      <div className="absolute inset-0">
        <img src={c.hero.img} alt={`${c.brand} concept hero`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/60 to-midnight/20" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">{c.hero.tagline}</p>
          <h2 className="font-sora font-extrabold tracking-tighter text-5xl sm:text-7xl leading-[0.98] mt-6 max-w-3xl">
            {c.hero.headline}
          </h2>
          <p className="mt-6 max-w-xl text-white/65 leading-relaxed">{c.hero.sub}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => scrollTo("csb-menu")} data-testid="csb-hero-menu" className="group inline-flex items-center gap-2 rounded-full bg-amber-500 text-midnight px-8 py-4 font-bold transition-transform duration-300 hover:scale-[1.04]">
              {c.hero.cta_menu}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollTo("csb-locations")} data-testid="csb-hero-locations" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-semibold text-white/85 transition-colors duration-300 hover:border-amber-400/70 hover:text-white">
              <MapPin className="w-5 h-5" /> {c.hero.cta_locations}
            </button>
          </div>
        </Reveal>
      </div>
    </section>

    {/* menu */}
    <section id="csb-menu" className="max-w-7xl mx-auto px-6 lg:px-10 py-28" data-testid="csb-menu">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">{c.menu.tagline}</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
          <h3 className="font-sora font-extrabold tracking-tighter text-4xl sm:text-5xl">{c.menu.heading}</h3>
          <p className="text-xs text-white/35 italic">{c.menu.note}</p>
        </div>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {c.menu.items.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.1}>
            <div className="group rounded-2xl border border-white/10 bg-surface overflow-hidden transition-transform duration-500 hover:-translate-y-1.5" data-testid={`csb-menu-item-${i}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-[1.06]" />
              </div>
              <div className="p-6">
                <div className="flex items-baseline justify-between gap-3">
                  <h4 className="font-sora font-bold tracking-tight">{m.name}</h4>
                  <span className="text-amber-400 font-sora font-bold text-sm shrink-0">${m.price}</span>
                </div>
                <p className="text-sm text-white/50 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10">
        <div className="flex flex-wrap items-center gap-3 text-sm text-white/55">
          <span className="text-xs uppercase tracking-[0.25em] text-white/40 mr-2">{c.menu.snacks_label}</span>
          {c.menu.snacks.map((s) => (
            <span key={s} className="border border-white/15 rounded-full px-4 py-1.5">{s}</span>
          ))}
        </div>
      </Reveal>
    </section>

    {/* story */}
    <section id="csb-story" className="relative overflow-hidden border-y border-white/10" data-testid="csb-story">
      <div className="grid lg:grid-cols-2">
        <div className="relative min-h-[24rem]">
          <img src={c.story.img} alt={`${c.brand} concept story`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-midnight/40" />
        </div>
        <div className="p-10 lg:p-20 flex flex-col justify-center bg-surface">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">{c.story.tagline}</p>
            <h3 className="font-sora font-extrabold tracking-tighter text-3xl sm:text-5xl mt-5 leading-[1.05]">
              {c.story.heading}
            </h3>
            <p className="mt-6 text-white/60 leading-relaxed">{c.story.body}</p>
            <div className="mt-8 rounded-2xl overflow-hidden border border-white/10 max-w-sm">
              <img src={c.story.kettle_img} alt={`${c.brand} concept detail`} loading="lazy" className="w-full aspect-[16/10] object-cover" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* locations */}
    <section id="csb-locations" className="max-w-7xl mx-auto px-6 lg:px-10 py-28" data-testid="csb-locations">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">{c.locations.tagline}</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
          <h3 className="font-sora font-extrabold tracking-tighter text-4xl sm:text-5xl">{c.locations.heading}</h3>
          <p className="text-xs text-white/35 italic">{c.locations.note}</p>
        </div>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {c.locations.items.map((l, i) => (
          <Reveal key={l.city} delay={i * 0.1}>
            <div className="glow-border rounded-2xl border border-white/10 bg-surface p-8 h-full" data-testid={`csb-location-${l.city.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <h4 className="font-sora font-bold text-2xl tracking-tight">{l.city}</h4>
                <span className="text-[10px] uppercase tracking-[0.2em] text-amber-400/80 border border-amber-400/30 rounded-full px-3 py-1">{l.area}</span>
              </div>
              <p className="text-sm text-white/50 mt-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-400" /> {l.address}</p>
              <p className="text-sm text-white/50 mt-2 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> {l.hours}</p>
              <button className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 hover:text-white transition-colors duration-300" data-testid={`csb-directions-${l.city.toLowerCase()}`}>
                {c.locations.directions} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* franchise */}
    <section id="csb-franchise" className="max-w-7xl mx-auto px-6 lg:px-10 pb-28" data-testid="csb-franchise">
      <Reveal>
        <div className="rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-500/10 to-transparent p-10 md:p-16">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">{c.franchise.tagline}</p>
          <div className="grid lg:grid-cols-2 gap-12 mt-6 items-start">
            <div>
              <h3 className="font-sora font-extrabold tracking-tighter text-3xl sm:text-5xl leading-[1.05]">
                {c.franchise.heading}
              </h3>
              <p className="mt-6 text-white/60 leading-relaxed max-w-md">{c.franchise.body}</p>
              <button data-testid="csb-franchise-cta" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-amber-500 text-midnight px-8 py-4 font-bold transition-transform duration-300 hover:scale-[1.04]">
                {c.franchise.cta}
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
            <div className="space-y-6">
              {c.franchise.steps.map((s) => (
                <div key={s.num} className="flex gap-6 items-start border-t border-white/10 pt-6">
                  <span className="font-sora font-extrabold text-3xl text-outline leading-none">{s.num}</span>
                  <div>
                    <h4 className="font-sora font-bold tracking-tight">{s.title}</h4>
                    <p className="text-sm text-white/50 mt-1.5 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>

    {/* concept footer */}
    <div className="border-t border-white/10 py-10 text-center px-6" data-testid="csb-concept-footer">
      <p className="font-sora font-bold tracking-tight text-white/80">
        {c.brand} <span className="text-amber-400">{c.brand_tag}</span> — {c.footer_line}
      </p>
      <p className="text-xs text-white/35 mt-2 max-w-md mx-auto">{c.footer_note}</p>
    </div>
  </div>
);

export default CsbConcept;
